import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ProgressBar } from '@fieldnation/platform-components';
import css from '../common/ReliabilityScore.scss';

import { dateStringToLocalTime } from '../../../MessageContainer/util';

import {
  TimelinessWorkOrderData,
  TimelinessScoreProps,
  ReliabilityScoreProps,
} from '../common/types/reliabilityScore.type';
import {
  TIMELINESS_CONSTANTS as CONSTANTS,
  TIMELINESS_TYPE_CONFIG,
  TIME_FORMAT_CONSTANTS,
  TIMELINESS_HARD_START_CONFIG,
  ON_TIME_CONSIDERATION,
  ScheduleType,
  TIMELINESS_SCORE_NOTES,
} from '../common/config';
import {
  convertToDate, getCheckInTime, getTimelinessTypeClass, isMyOwnProfile,
  isStaff,
} from '../common/utils';
import Badge from '../common/components/Badge';

interface WorkOrderRowProps {
  item: TimelinessWorkOrderData;
}

const TimeLinessItem: React.FC<ReliabilityScoreProps> = React.memo((props) => {
  const {
    label, count, color, totalAssignments = 0,
  } = props;

  return (
    <div className={css['timeliness-item']}>
      <div className={css['timeliness-label']}>{label}</div>
      <div className={css['back-out-bar']}>
        <ProgressBar
          currentProgress={count}
          maxProgress={totalAssignments}
          fillColor={color}
        />
      </div>
      <span className={css['timeliness-count']}>{count}</span>
    </div>
  );
});

TimeLinessItem.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  count: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  title: PropTypes.string,
  totalAssignments: PropTypes.number,
} as any;

TimeLinessItem.defaultProps = {
  description: '',
  title: '',
  totalAssignments: 0,
} as any;

const TimelinessDescription: React.FC = () => (
  <div className={css['timeliness-description']}>
    <div className={css['on-time-title']}>{ON_TIME_CONSIDERATION}</div>
    <ul className={css['on-time-list']}>
      {Object.values(TIMELINESS_HARD_START_CONFIG).map(({ label, description }) => (
        <li key={label}>
          <span className={css['bold-label']}>{label}</span>
          :
          {' '}
          <span className={css['description-text']}>{description}</span>
        </li>
      ))}
      <li key="timeliess_notes">
        <span className={css['bold-label']}>Note</span>
        :
        {' '}
        <span className={css['description-text']}>{TIMELINESS_SCORE_NOTES}</span>
      </li>
    </ul>
  </div>
);

const getScheduleType = (scheduleType: string, arrivalTime: string) => {
  if (scheduleType === ScheduleType.HOURS || scheduleType === ScheduleType.RANGE) {
    return CONSTANTS.SCHEDULE_TYPE.HOURS;
  }
  return dateStringToLocalTime(arrivalTime, TIME_FORMAT_CONSTANTS.TIME_12_HOUR);
};

const WorkOrderRow: React.FC<WorkOrderRowProps> = React.memo(({ item }) => {
  const formattedDate = useMemo(
    () => convertToDate(item?.checkedInAt
      ?.utc),
    [item?.checkedInAt
      ?.utc],
  );

  const typeClass = getTimelinessTypeClass(item?.type);
  const badgeClassName = `${css['type-timeliness-badge']} ${css[typeClass]}`;

  return (
    <tr>
      <td>{formattedDate}</td>
      <td>{item?.workOrderId}</td>
      <td>
        {getScheduleType(item?.scheduleType, item?.arrivalTime?.utc)}
      </td>
      <td>
        <Badge type={getCheckInTime(item)} classType={badgeClassName} />
      </td>
    </tr>
  );
});

WorkOrderRow.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number,
    date: PropTypes.string,
    workOrder: PropTypes.string,
    workOrderTitle: PropTypes.string,
    title: PropTypes.string,
    workOrderId: PropTypes.number,
    type: PropTypes.oneOf(['on_time', 'late', 'early']),
    scheduleType: PropTypes.string,
    checkedInAt: PropTypes.shape({
      utc: PropTypes.string,
    }),
    arrivalTime: PropTypes.shape({
      utc: PropTypes.string,
    }),
  }),
} as any;

const TimelinessScore: React.FC<TimelinessScoreProps> = (props) => {
  const { timelinessData, providerId, onSupportLinkClick } = props;

  const totalTimeLog = useMemo(
    () => timelinessData?.details?.totalTimeLog ?? 0,
    [timelinessData?.details?.totalTimeLog],
  );

  const isMyProfile = useMemo(() => isMyOwnProfile(providerId), [providerId]);

  const handleSupportClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onSupportLinkClick) {
      e.preventDefault();
      onSupportLinkClick(e);
    }
  };

  const timelinessCounts = useMemo(() => ({
    totalEarly: timelinessData?.details?.totalEarly ?? 0,
    totalLate: timelinessData?.details?.totalLate ?? 0,
    totalOnTime: timelinessData?.details?.totalOnTime ?? 0,
  }), [
    timelinessData?.details?.totalEarly,
    timelinessData?.details?.totalLate,
    timelinessData?.details?.totalOnTime,
  ]);

  const workOrders = useMemo(
    () => [
      ...(timelinessData?.details?.lateCheckedInData || []),
      ...(timelinessData?.details?.earlyCheckedInData || []),
    ],
    [timelinessData?.details?.lateCheckedInData, timelinessData?.details?.earlyCheckedInData],
  );

  const tableVisibility = (isMyProfile || isStaff) && !!workOrders?.length;

  const TimeLinessTypes = useMemo(() => [
    {
      ...TIMELINESS_TYPE_CONFIG.ON_TIME,
      count: timelinessCounts.totalOnTime,
    },
    {
      ...TIMELINESS_TYPE_CONFIG.TOO_EARLY,
      count: timelinessCounts.totalEarly,
    },
    {
      ...TIMELINESS_TYPE_CONFIG.LATE,
      count: timelinessCounts.totalLate,
    },
  ], [timelinessCounts.totalEarly, timelinessCounts.totalLate, timelinessCounts.totalOnTime]);

  return (
    <>
      <div className={css['work-order-timeliness']} style={!tableVisibility ? { height: 'auto' } : undefined}>
        <div className={css['timeliness-section']}>
          <div className={css['timeliness-items']}>
            {TimeLinessTypes.map((item) => (
              <TimeLinessItem key={item?.label} {...item} totalAssignments={totalTimeLog} />
            ))}
          </div>
          <TimelinessDescription />
        </div>

        {tableVisibility && (
        <div className={css['table-container']}>
          <div className={css['table-wrapper']}>
            <table className={css['timeliness-order-table']}>
              <thead>
                <tr>
                  <th>{CONSTANTS.TABLE_HEADERS.DATE}</th>
                  <th>{CONSTANTS.TABLE_HEADERS.WORK_ORDER}</th>
                  <th>{CONSTANTS.TABLE_HEADERS.TYPE}</th>
                  <th>{CONSTANTS.TABLE_HEADERS.WORK_ORDER_TITLE}</th>
                </tr>
              </thead>
              <tbody>
                {workOrders.map((item) => (
                  <WorkOrderRow key={item?.id} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

      </div>
      {isMyProfile && (
      <div className={css['warning-message']}>
        {CONSTANTS.MESSAGES.WARNING}
        {' '}
        <a href={CONSTANTS.MESSAGES.LINK_URL} target="_blank" className={css['warning-link']} rel="noreferrer" onClick={handleSupportClick}>
          {CONSTANTS.MESSAGES.LINK_TEXT}
        </a>
      </div>
      )}
    </>
  );
};

TimelinessScore.propTypes = {
  timelinessData: PropTypes.shape({
    details: PropTypes.shape({
      onTimeCheckedInData: PropTypes.arrayOf(PropTypes.object),
      totalEarly: PropTypes.number,
      totalLate: PropTypes.number,
      totalOnTime: PropTypes.number,
      totalTimeLog: PropTypes.number,
    }),
  }),
  providerId: PropTypes.number.isRequired,
  onSupportLinkClick: PropTypes.func,
} as any;

TimelinessScore.defaultProps = {
  timelinessData: undefined,
  onSupportLinkClick: undefined,
} as any;

export default React.memo(TimelinessScore);
